import { AccessTime, CalendarToday } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";

import { formatLocalTime, getTimezoneLabel } from "~constants/timezones";

const tRegion = "Region";
const tSource = "Source";
const tEstimatedValue = "Estimated Value";
const tExpectedCloseDate = "Expected Close Date";
const tTimeZone = "Time Zone";
const tLocalTime = "Local Time";

interface ProspectAdditionalDetailsProps {
  region?: string;
  source?: string;
  estimatedValue?: number;
  expectedCloseDate?: string;
  timeZone?: string;
}

export const ProspectAdditionalDetails = ({
  region,
  source,
  estimatedValue,
  expectedCloseDate,
  timeZone,
}: ProspectAdditionalDetailsProps) => {
  if (!region && !source && !estimatedValue && !expectedCloseDate && !timeZone) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: "action.hover",
        borderRadius: 1,
        display: "grid",
        gap: 2,
        p: 2,
      }}
    >
      {region && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {tRegion}
          </Typography>
          <Typography variant="body1">{region}</Typography>
        </Box>
      )}
      {source && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {tSource}
          </Typography>
          <Typography variant="body1">{source}</Typography>
        </Box>
      )}
      {estimatedValue && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {tEstimatedValue}
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            {new Intl.NumberFormat("en-US", {
              currency: "USD",
              style: "currency",
            })
              .format(estimatedValue)
              .replace("$", "$ ")}
          </Typography>
        </Box>
      )}
      {expectedCloseDate && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {tExpectedCloseDate}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarToday fontSize="small" color="info" />
            <Typography variant="body1">
              {DateTime.fromISO(expectedCloseDate).setZone("UTC").toFormat("M/d/yyyy")}
            </Typography>
          </Box>
        </Box>
      )}
      {timeZone && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {tTimeZone}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <AccessTime fontSize="small" color="secondary" />
            <Typography variant="body1">{getTimezoneLabel(timeZone)}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {tLocalTime}: {formatLocalTime(timeZone)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
